// 文件传输协议（走独立的 WebRTC DataChannel，不打断控制通道）。
// 首次发送：file-meta(JSON) → 若干 ArrayBuffer 分块(≤CHUNK) → file-end(JSON)
// 接收端按分块发送 file-ack，完成保存后发送 done=true 的完成回执。
// 断线恢复：file-resume-request → file-resume → 带 offset 的 file-meta → 剩余分块 → file-end
// 接收方：校验大小和 SHA-256 后合并保存。file-cancel 可由任一端发送。

import type { ClipboardFileKind } from './clipboard-file';

export const FILE_CHANNEL = 'file';
export const FILE_CHUNK = 64 * 1024; // up to 64KB; sender caps this to negotiated SCTP support
export const FILE_COMPAT_CHUNK = 16 * 1024;
// 预读少量分块，让磁盘/Blob 读取与 RTC 背压等待重叠；发送顺序仍由
// DataChannel 保证，内存上限约为 1MB，不会把大文件一次性读入内存。
export const FILE_READ_AHEAD_CHUNKS = 16;
export const MAX_FILE_TRANSFER_BYTES = 1024 * 1024 * 1024; // 1GB
export const MAX_FILE_NAME_LENGTH = 255;
export const FILE_TRANSFER_MAX_RETRIES = 1;
export const FILE_TRANSFER_RETRY_DELAY_MS = 350;
// RTC 文件通道在 ICE/信令恢复后可能比控制通道晚几百毫秒到数秒才重新打开。
// 发送端等待通道恢复，而不是在第一个 close 事件后立刻把传输判定为失败。
export const FILE_TRANSFER_CHANNEL_WAIT_TIMEOUT_MS = 20_000;
export const FILE_TRANSFER_CHANNEL_POLL_MS = 100;
// 仅在同一端进程仍保留未完成任务时尝试续传；旧版本不认识这两类消息，
// 发送端会在短暂等待后自动回退到完整重传。
export const FILE_RESUME_TIMEOUT_MS = 450;
export const FILE_RESUME_GRACE_MS = 90_000;
// 接收端每确认约 256KB 的已写入数据发送一次轻量回执；回执只走文件通道，
// 不会把实时键鼠通道变成等待队列。完成回执用于新版本确认“已保存”，
// 没有回执的旧版本会在短暂等待后按兼容路径继续。
export const FILE_ACK_INTERVAL_BYTES = 256 * 1024;
export const FILE_COMPLETION_ACK_TIMEOUT_MS = 2_500;
export const FILE_ACK_SUPPORT_TIMEOUT_MS = 1_000;
export const FILE_ACK_WINDOW_HIGH_BYTES = 4 * 1024 * 1024;
export const FILE_ACK_WINDOW_LOW_BYTES = 2 * 1024 * 1024;
export const FILE_ACK_WAIT_TIMEOUT_MS = 30_000;

/**
 * 将连续字节偏移映射成已完成的确认块数。确认块只用于恢复和诊断，
 * 不会改变 WebRTC 文件通道本身的可靠、有序传输语义。
 */
export function fileAckBlockIndex(offset: number): number {
  if (!Number.isSafeInteger(offset) || offset <= 0) return 0;
  return Math.ceil(offset / FILE_ACK_INTERVAL_BYTES);
}

/** Honor the negotiated peer limit, while using larger messages when both peers allow it. */
export function fileChunkSize(maxMessageSize?: number | null): number {
  if (maxMessageSize === undefined || maxMessageSize === null || maxMessageSize === 0) {
    return FILE_COMPAT_CHUNK;
  }
  if (maxMessageSize === Number.POSITIVE_INFINITY) return FILE_CHUNK;
  if (!Number.isFinite(maxMessageSize) || maxMessageSize < 0) return FILE_COMPAT_CHUNK;
  return Math.max(1, Math.min(FILE_CHUNK, Math.floor(maxMessageSize)));
}

export interface FileTransferPersistMarker {
  transferred: number;
  at: number;
}

/** Avoid synchronous storage/server-task work for every incoming data-channel chunk. */
export function shouldPersistFileTransferProgress(
  progress: Pick<FileTransferProgress, 'transferred' | 'state'>,
  marker: FileTransferPersistMarker | undefined,
  now = Date.now(),
): boolean {
  if (progress.state !== 'transferring' || !marker) return true;
  return progress.transferred - marker.transferred >= 512 * 1024 || now - marker.at >= 1_000;
}

export type FileTransferPurpose = 'download' | 'clipboard';
export type FileTransferDirection = 'send' | 'receive';
export type FileTransferState = 'transferring' | 'completed' | 'failed' | 'cancelled';

export interface FileTransferProgress {
  id: string;
  name: string;
  size: number;
  transferred: number;
  direction: FileTransferDirection;
  state: FileTransferState;
  error?: string;
  /** 发送端源文件仍需用户重新选择时为 true；不保存文件内容。 */
  recoverable?: boolean;
}

/**
 * 生成当前发送任务生命周期内使用的文件编号。重试和短暂重连沿用同一编号，
 * 这样 UI 不会堆出重复记录；接收端会用编号、文件名和大小再次确认，避免旧
 * 任务被误接到新文件上。发送端进程重启后不会伪造旧源文件，因此不会自动续接。
 */
export function createFileTransferId(prefix = 'file'): string {
  const safePrefix = prefix.replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 24) || 'file';
  return `${safePrefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface FileMeta {
  type: 'file-meta';
  id: string;
  name: string;
  size: number;
  /** 新版续传时从接收端已确认的偏移开始发送。 */
  offset?: number;
  resume?: boolean;
  purpose?: FileTransferPurpose;
  clipboardId?: string;
  clipboardIndex?: number;
  clipboardTotal?: number;
  kind?: ClipboardFileKind;
  mimeType?: string;
}

export interface FileResumeRequest {
  type: 'file-resume-request';
  id: string;
  name: string;
  size: number;
  purpose?: FileTransferPurpose;
  clipboardId?: string;
  clipboardIndex?: number;
  clipboardTotal?: number;
  kind?: ClipboardFileKind;
  mimeType?: string;
  /** 发送端最近一次收到的连续确认点，仅作为恢复提示，接收端仍以本地状态为准。 */
  checkpointOffset?: number;
}

export interface FileResumeResponse {
  type: 'file-resume';
  id: string;
  offset: number;
  accepted: boolean;
  reason?: string;
}

export interface FileAck {
  type: 'file-ack';
  id: string;
  /** 接收端已经处理/写入的连续字节偏移。 */
  offset: number;
  /** 已完成的确认块数；旧版本可不提供，offset 仍是唯一的恢复依据。 */
  blockIndex?: number;
  /** true 表示文件已经完成校验并保存；未提供时只是中间进度确认。 */
  done?: boolean;
  /** 完成回执是否成功；中间进度回执不需要该字段。 */
  accepted?: boolean;
  reason?: string;
}

export interface FileEnd {
  type: 'file-end';
  id: string;
  // 兼容旧版发送端：旧版没有摘要时仍可按大小完成，但新版始终发送。
  sha256?: string;
}

export interface FileCancel {
  type: 'file-cancel';
  id: string;
  reason?: string;
}

export interface FileClipboardAck {
  type: 'file-clipboard-ack';
  id: string;
  ok: boolean;
  reason?: string;
}

export type FileControl = FileMeta | FileResumeRequest | FileResumeResponse | FileAck | FileEnd | FileClipboardAck | FileCancel;

// 浏览器和 Electron renderer 都可以使用的增量 SHA-256，避免为了计算摘要
// 再把整个 File 一次性读入内存。文件通道仍按小块读取，摘要在发送/接收
// 的同一条数据路径上计算。
const SHA256_K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

function writeUint32BE(target: Uint8Array, offset: number, value: number): void {
  target[offset] = value >>> 24;
  target[offset + 1] = value >>> 16;
  target[offset + 2] = value >>> 8;
  target[offset + 3] = value;
}

/** 小块增量 SHA-256；digestHex() 只应在 update 完成后调用一次。 */
export class IncrementalSha256 {
  private readonly state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly block = new Uint8Array(64);
  private readonly words = new Uint32Array(64);
  private blockLength = 0;
  private byteLength = 0;
  private result: Uint8Array | null = null;

  update(input: ArrayBuffer | ArrayBufferView): this {
    if (this.result) throw new Error('SHA-256 已完成，不能继续写入');
    const bytes = input instanceof ArrayBuffer
      ? new Uint8Array(input)
      : new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
    this.byteLength += bytes.byteLength;
    let offset = 0;
    while (offset < bytes.byteLength) {
      const take = Math.min(64 - this.blockLength, bytes.byteLength - offset);
      this.block.set(bytes.subarray(offset, offset + take), this.blockLength);
      this.blockLength += take;
      offset += take;
      if (this.blockLength === 64) {
        this.compress(this.block);
        this.blockLength = 0;
      }
    }
    return this;
  }

  digest(): Uint8Array {
    if (this.result) return this.result.slice();
    const bitLength = this.byteLength * 8;
    this.block[this.blockLength++] = 0x80;
    if (this.blockLength > 56) {
      this.block.fill(0, this.blockLength);
      this.compress(this.block);
      this.blockLength = 0;
    }
    this.block.fill(0, this.blockLength, 56);
    // 当前传输上限远小于 2^53 字节，使用安全整数拆成高低 32 位。
    const high = Math.floor(bitLength / 0x100000000);
    const low = bitLength >>> 0;
    writeUint32BE(this.block, 56, high);
    writeUint32BE(this.block, 60, low);
    this.compress(this.block);
    const result = new Uint8Array(32);
    this.state.forEach((value, index) => writeUint32BE(result, index * 4, value));
    this.result = result;
    return result.slice();
  }

  digestHex(): string {
    return Array.from(this.digest(), (value) => value.toString(16).padStart(2, '0')).join('');
  }

  private compress(block: Uint8Array): void {
    for (let index = 0; index < 16; index += 1) {
      const offset = index * 4;
      this.words[index] = (
        (block[offset] << 24) |
        (block[offset + 1] << 16) |
        (block[offset + 2] << 8) |
        block[offset + 3]
      ) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const w = this.words[index - 15];
      const v = this.words[index - 2];
      const small0 = rotateRight(w, 7) ^ rotateRight(w, 18) ^ (w >>> 3);
      const small1 = rotateRight(v, 17) ^ rotateRight(v, 19) ^ (v >>> 10);
      this.words[index] = (this.words[index - 16] + small0 + this.words[index - 7] + small1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = this.state;
    for (let index = 0; index < 64; index += 1) {
      const big1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + big1 + choice + SHA256_K[index] + this.words[index]) >>> 0;
      const big0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (big0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    this.state[0] = (this.state[0] + a) >>> 0;
    this.state[1] = (this.state[1] + b) >>> 0;
    this.state[2] = (this.state[2] + c) >>> 0;
    this.state[3] = (this.state[3] + d) >>> 0;
    this.state[4] = (this.state[4] + e) >>> 0;
    this.state[5] = (this.state[5] + f) >>> 0;
    this.state[6] = (this.state[6] + g) >>> 0;
    this.state[7] = (this.state[7] + h) >>> 0;
  }
}

export class FileTransferCancelledError extends Error {
  readonly code = 'FILE_TRANSFER_CANCELLED';

  constructor(message = '文件传输已取消') {
    super(message);
    this.name = 'FileTransferCancelledError';
  }
}

/**
 * Wait for the RTCDataChannel queue to fall below its low-water mark. The sender
 * still checks the high-water mark before each chunk, but waiting is driven by
 * bufferedamountlow instead of sleeping once per 16 KB chunk. A light poll is
 * retained only to make cancellation and older WebRTC implementations responsive.
 */
export async function waitForFileChannelDrain(
  channel: RTCDataChannel,
  highWaterMark: number,
  lowWaterMark: number,
  isCancelled: () => boolean,
  timeoutMs = 15_000,
): Promise<void> {
  if (channel.readyState !== 'open') throw new Error('文件传输通道已断开');
  if (channel.bufferedAmount <= highWaterMark) return;

  const target = Math.min(lowWaterMark, highWaterMark);
  const startedAt = Date.now();
  while (channel.bufferedAmount > target) {
    if (isCancelled()) throw new FileTransferCancelledError();
    if (channel.readyState !== 'open') throw new Error('文件传输通道已断开');
    const remainingMs = timeoutMs - (Date.now() - startedAt);
    if (remainingMs <= 0) throw new Error('文件传输通道拥塞，请稍后重试');

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let pollTimer: ReturnType<typeof setInterval> | undefined;
      let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
      const cleanup = (): void => {
        if (pollTimer !== undefined) clearInterval(pollTimer);
        if (timeoutTimer !== undefined) clearTimeout(timeoutTimer);
        channel.removeEventListener('bufferedamountlow', check);
        channel.removeEventListener('close', onClose);
      };
      const finish = (error?: Error): void => {
        if (settled) return;
        settled = true;
        cleanup();
        if (error) reject(error);
        else resolve();
      };
      const check = (): void => {
        if (isCancelled()) return finish(new FileTransferCancelledError());
        if (channel.readyState !== 'open') return finish(new Error('文件传输通道已断开'));
        if (channel.bufferedAmount <= target) finish();
      };
      const onClose = (): void => finish(new Error('文件传输通道已断开'));

      channel.addEventListener('bufferedamountlow', check);
      channel.addEventListener('close', onClose, { once: true });
      pollTimer = setInterval(check, 100);
      timeoutTimer = setTimeout(
        () => finish(new Error('文件传输通道拥塞，请稍后重试')),
        remainingMs,
      );
      // Recheck after listener registration so a drain between the initial
      // bufferedAmount check and listener setup cannot leave this wait asleep.
      check();
    });
  }

  if (isCancelled()) throw new FileTransferCancelledError();
  if (channel.readyState !== 'open') throw new Error('文件传输通道已断开');
}

/** Wait for receiver-confirmed bytes, not merely bytes queued by the local browser. */
export async function waitForFileAcknowledgedOffset(
  channel: RTCDataChannel,
  getAcknowledgedOffset: () => number | undefined,
  minimumOffset: number,
  isCancelled: () => boolean,
  timeoutMs: number,
): Promise<number | null> {
  const startedAt = Date.now();
  while (true) {
    if (isCancelled()) throw new FileTransferCancelledError();
    if (channel.readyState !== 'open') throw new Error('文件传输通道已断开');
    const offset = getAcknowledgedOffset();
    if (offset !== undefined && offset >= minimumOffset) return offset;
    if (Date.now() - startedAt >= timeoutMs) {
      if (minimumOffset === 0) return null;
      throw new Error('等待对端接收确认超时');
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
  }
}

export interface FileTransferRateState {
  speedBps: number;
  lastProgressAt: number;
  sampleAt: number;
  sampleBytes: number;
}

export const FILE_TRANSFER_RATE_SAMPLE_MS = 750;
export const FILE_TRANSFER_RATE_STALE_MS = 1_500;

/** Estimate a real progress window, not a per-render/per-chunk burst. */
export function updateFileTransferRate(
  previous: (FileTransferProgress & FileTransferRateState) | undefined,
  progress: Pick<FileTransferProgress, 'state' | 'transferred'>,
  now: number,
): FileTransferRateState {
  if (progress.state !== 'transferring') {
    return { speedBps: 0, lastProgressAt: now, sampleAt: now, sampleBytes: progress.transferred };
  }

  if (!previous || previous.state !== 'transferring' || progress.transferred < previous.transferred) {
    return { speedBps: 0, lastProgressAt: now, sampleAt: now, sampleBytes: progress.transferred };
  }

  const hasProgress = progress.transferred > previous.transferred;
  if (!hasProgress) return previous;

  // A new burst after a long pause starts a fresh sample. Do not blend the old
  // high rate into the ETA after the connection has been idle.
  if (now - previous.lastProgressAt > FILE_TRANSFER_RATE_STALE_MS) {
    return { speedBps: 0, lastProgressAt: now, sampleAt: now, sampleBytes: progress.transferred };
  }

  const elapsedMs = now - previous.sampleAt;
  if (elapsedMs < FILE_TRANSFER_RATE_SAMPLE_MS) {
    return { ...previous, lastProgressAt: now };
  }

  const sampleBytes = Math.max(0, progress.transferred - previous.sampleBytes);
  const instantSpeed = sampleBytes * 1000 / elapsedMs;
  const speedBps = Number.isFinite(instantSpeed)
    ? previous.speedBps > 0 ? previous.speedBps * 0.5 + instantSpeed * 0.5 : instantSpeed
    : 0;
  return {
    speedBps,
    lastProgressAt: now,
    sampleAt: now,
    sampleBytes: progress.transferred,
  };
}

export function isFileTransferProgressStale(rate: FileTransferRateState, now: number): boolean {
  return now - rate.lastProgressAt > FILE_TRANSFER_RATE_STALE_MS;
}

/** 只对通道短暂不可用、拥塞或连接波动做自动重试，用户取消和数据错误不重试。 */
export function isRetryableFileTransferError(error: unknown): boolean {
  if (error instanceof FileTransferCancelledError) return false;
  const message = error instanceof Error ? error.message : String(error || '');
  const name = error instanceof Error ? error.name : '';
  if (/超过 1GB|文件名无效|编号无效|读取文件失败|大小异常|校验失败|不完整/.test(message)) return false;
  return /通道|连接|拥塞|超时|readyState|not open|closed|network|datachannel|invalidstate|operationerror/i.test(
    `${name} ${message}`,
  );
}
